'use client';

import * as React from 'react';
import { isActivationKey } from '@federa/const/keyboard-events';
import type { KeyboardActivationEvent, UseKeyboardClickOptions, UseKeyboardClickReturn } from '@federa/types/react-hook-state';

/** Keyboard click handling (Enter/Space) for accessibility; returns click and keydown handlers. */
export function useKeyboardClick({ onClick, disabled }: UseKeyboardClickOptions): UseKeyboardClickReturn {
	const handleClick = React.useCallback(() => {
		if (!disabled) {
			onClick();
		}
	}, [disabled, onClick]);

	const handleKeyDown = React.useCallback(
		(e: KeyboardActivationEvent) => {
			// Own-target ONLY. Without this the handler also fires for keystrokes BUBBLING from a nested interactive
			// element, and its `preventDefault()` suppresses that element's own synthesized click -- so on a clickable
			// table row containing a button, Enter ran the ROW's action instead of the button's, and Space ran both
			// (row on keydown, button on keyup). Mouse callers are unaffected: they stop propagation at the child.
			if (e.target !== e.currentTarget) return;
			if (isActivationKey(e.key)) {
				e.preventDefault();
				handleClick();
			}
		},
		[handleClick],
	);

	return { handleClick, handleKeyDown };
}

/** The always-enabled keyboard click that calls `handler(arg)` -- a sortable header's column, a row's id, a step
 *  heading's step: one spelling for every surface that binds one argument into its action. */
export function useBoundKeyboardClick<A>(handler: (arg: A) => void, arg: A): UseKeyboardClickReturn {
	return useKeyboardClick({ onClick: () => handler(arg) });
}
